import type {LeadTag, OrderOpt} from "~/types/Events";

export const useContactsStore = defineStore('ContactsStore', () => {
    const {$api} = useNuxtApp();

    const leads : Ref<EventLead[]> = ref([]);
    const page = ref(1);
    const loading = ref(false);
    const lastPage = ref(1);
    const noData = ref(false);
    const lastEventId = ref(0);
    const refetchData = ref(true);
    const currentFilter = ref<OrderOpt>('newest');
    const filterUsed = ref<boolean>(false);
    const currentSearch = ref('');
    const totalLeads = ref(0);

    const canLoadMore = computed((): boolean => {
        return page.value <= lastPage.value && !noData.value;
    })

    // Replaces the cached entry for this lead if present, else prepends it — keeps
    // `leads` from ever serving a stale copy of a lead whose tags/notes changed server-side.
    const upsertLead = (lead: EventLead) => {
        const idx = leads.value.findIndex(l => l.id === lead.id);
        if(idx !== -1) {
            leads.value[idx] = lead;
        } else {
            leads.value = [lead, ...leads.value];
        }
    }

    const loadContacts = async (eventId: number, order: OrderOpt, shouldResetState = false, search?: string) => {
        filterUsed.value = true;
        currentFilter.value = order;
        if(search !== undefined && search !== currentSearch.value) {
            currentSearch.value = search;
            shouldResetState = true;
        }
        if(shouldResetState || lastEventId.value !== eventId) {
            resetState();
        }
        if(loading.value || !canLoadMore.value) {
            return;
        }
        loading.value = true;
        try {
            const res = await $api.v2.getEventLeadsPlain(eventId, order, page.value, currentSearch.value);
            const data = res.data;
            leads.value = page.value === 1 ? data : [...leads.value, ...data];
            if(data.length > 0) {
                lastPage.value = res.meta.last_page;
                page.value++;
            }
            if(!currentSearch.value) {
                totalLeads.value = res.meta.total;
            }
            noData.value = res.meta.total === 0;
        } catch (error) {
            console.error(error);
        } finally {
            loading.value = false
        }
    }

    const loadContact = async (eventId: number, contactId: number):Promise<EventLead | undefined> => {
        try {
            const resp:EventLead = await $api.v2.getEventLead(eventId, contactId);
            upsertLead(resp);

            return resp;
        } catch (error) {
            console.error(error);
        }
    }

    // Available tag options for the user's company at the event (partner-portal managed)
    const tagOptions: Ref<LeadTag[]> = ref([]);
    const tagOptionsEventId = ref(0);
    const loadTagOptions = async (eventId: number) => {
        if(tagOptionsEventId.value === eventId) {
            return tagOptions.value;
        }
        try {
            tagOptions.value = await $api.v2.getEventLeadTags(eventId);
            tagOptionsEventId.value = eventId;
        } catch (error) {
            // Offline or request failure — leave options empty, the page falls back to the lead's own tags
            tagOptions.value = [];
        }

        return tagOptions.value;
    }

    const showNewScanMessage: Ref<boolean|null> = ref(null);
    const scanContact = async(eventId: number, code: string) => {
        const lead = await $api.v2.scanEventLead(eventId, code);
        upsertLead(lead);
        showNewScanMessage.value = lead.new_scan;

        // A new (not previously scanned) badge creates a lead — reflect it in the counter
        // immediately instead of waiting for the leads list to re-fetch.
        if(lead.new_scan) {
            totalLeads.value++;
        }

        return lead;
    }

    const cleanScanMessage = () => {
        showNewScanMessage.value = null;
    }

    const updateContact = async (eventId: number, contactId: number, leadAdditionalInfo: string, tagsAdd: Array<number>, tagsRemove: Array<number>) => {
        const resp = await $api.v2.updateContact(eventId, contactId, { additional_info: leadAdditionalInfo, tags_add: tagsAdd, tags_remove: tagsRemove });
        upsertLead(resp);

        return resp;
    }

    const resetState = () => {
        leads.value = [];
        page.value = 1;
        lastPage.value = 1;
        noData.value = false;
        loading.value = false;
        refetchData.value = true;
    }

    return {
        leads,
        lastEventId,
        canLoadMore,
        loading,
        refetchData,
        currentFilter,
        filterUsed,
        currentSearch,
        totalLeads,
        tagOptions,
        loadTagOptions,
        showNewScanMessage,
        cleanScanMessage,
        loadContacts,
        loadContact,
        scanContact,
        updateContact,
        upsertLead,
        resetState,
    }
}, {},)
